import {
  CONSENT_PURPOSES,
  CONSENT_SOURCES,
  LEGAL_BASES,
  REVOCATION_BUSINESS_DAYS,
  consentSchema,
  formatDate,
  type ConsentPurpose,
  type ConsentSource,
  type LegalBasis,
  type PrivacyStatusInput,
} from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState } from 'react';

import { api, errorMessage } from '../../lib/api.ts';
import { contactKeys, type ContactDetail } from '../../lib/contacts-api.ts';
import { formatDay, privacyKeys, useConsents, type Consent } from '../../lib/privacy-api.ts';
import { useContactRequests } from '../../lib/privacy-requests-api.ts';
import { useCan, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { ContactRightsActions, PendingReview } from '../privacy/contact-rights.tsx';
import { RegisterRequestDialog, RequestBriefList } from '../privacy/requests.tsx';
import { Alert } from '../ui/alert.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { Dialog } from '../ui/dialog.tsx';
import { Field, Input, Select } from '../ui/field.tsx';

const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

/** Banners on top of the contact: nobody should act on a limited or disputed record by accident. */
export function PrivacyBanners({ contact }: { contact: ContactDetail }) {
  const { data: session } = useSession();
  const { limited, rectification, noticePending, anonymizedAt } = contact.privacy;
  if (anonymizedAt) {
    return (
      <Alert tone="warning" title="Contacto anonimizado">
        Se anonimizó el {formatDate(anonymizedAt, session?.tenant.timezone)} por solicitud del
        titular: no se puede editar ni contactar. Se conserva solo para el historial de negocios y
        comisiones.
        <PendingReview contactId={contact.id} />
      </Alert>
    );
  }
  return (
    <>
      {noticePending && <NoticePendingBanner contact={contact} />}
      {limited && (
        <Alert tone="warning" title="Tratamiento limitado">
          Mientras dure la limitación los datos solo se conservan: no se editan, no se contactan y
          no se comparten.{limited.note ? ` Motivo: ${limited.note}` : ''}
        </Alert>
      )}
      {rectification && (
        <Alert tone="info" title="En proceso de rectificación">
          La persona pidió corregir sus datos; verifica antes de usarlos.
          {rectification.note ? ` Dato en revisión: ${rectification.note}` : ''}
        </Alert>
      )}
    </>
  );
}

/** Imported contacts: the privacy notice has not been given yet. */
function NoticePendingBanner({ contact }: { contact: ContactDetail }) {
  const client = useQueryClient();
  const notify = useToast();
  const [saving, setSaving] = useState(false);
  const delivered = async () => {
    setSaving(true);
    try {
      await api(`/contacts/${contact.id}/privacy`, {
        method: 'PATCH',
        body: { noticeDelivered: true },
      });
      await client.invalidateQueries({ queryKey: contactKeys.all });
      notify('Se registró que recibió el aviso de privacidad.', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Alert tone="info" title="Pendiente de aviso de privacidad">
      Se importó de la cartera anterior y aún no ha recibido el aviso de privacidad. Entrégaselo en
      el próximo contacto.
      {contact.can.edit && (
        <div className="mt-2">
          <Button size="sm" variant="secondary" loading={saving} onClick={() => void delivered()}>
            Marcar aviso entregado
          </Button>
        </div>
      )}
    </Alert>
  );
}

/** ARCO-POL requests of the person: anyone who sees the contact registers one. */
function ContactRequests({ contact }: { contact: ContactDetail }) {
  const requests = useContactRequests(contact.id);
  const [registering, setRegistering] = useState(false);
  return (
    <div className="space-y-2 border-t border-border pt-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium">Solicitudes sobre sus datos (ARCO-POL)</h3>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setRegistering(true);
          }}
        >
          <Plus /> Registrar solicitud
        </Button>
      </div>
      <RequestBriefList items={requests.data ?? []} empty="Ninguna." />
      {registering && (
        <RegisterRequestDialog
          contact={{ id: contact.id, name: contact.name }}
          onClose={() => {
            setRegistering(false);
          }}
        />
      )}
    </div>
  );
}

type StatusKey = 'rectification' | 'limited' | 'marketingOptOut';

const STATUS_TEXT: Record<StatusKey, { label: string; on: string; off: string; note?: string }> = {
  rectification: {
    label: 'En proceso de rectificación',
    on: 'Marcar',
    off: 'Terminar',
    note: 'Qué dato se está corrigiendo',
  },
  limited: {
    label: 'Tratamiento limitado',
    on: 'Limitar',
    off: 'Levantar',
    note: 'Motivo de la limitación',
  },
  marketingOptOut: { label: 'No desea recibir marketing', on: 'Marcar', off: 'Quitar' },
};

function statusBody(key: StatusKey, on: boolean, note: string): PrivacyStatusInput {
  if (key === 'rectification')
    return { rectification: on, ...(on ? { rectificationNote: note } : {}) };
  if (key === 'limited') return { processingLimited: on, ...(on ? { limitationNote: note } : {}) };
  return { marketingOptOut: on };
}

export function PrivacyCard({ contact }: { contact: ContactDetail }) {
  const { data: session } = useSession();
  const timeZone = session?.tenant.timezone;
  const canManage = useCan('privacy.manage');
  const client = useQueryClient();
  const notify = useToast();
  const consents = useConsents(contact.id);
  const [asking, setAsking] = useState<StatusKey | null>(null);
  const [adding, setAdding] = useState(false);
  const [revoking, setRevoking] = useState<Consent | null>(null);

  const isLimited = contact.privacy.limited !== null;
  const since: Record<StatusKey, string | null> = {
    rectification: contact.privacy.rectification?.since ?? null,
    limited: contact.privacy.limited?.since ?? null,
    marketingOptOut: contact.privacy.marketingOptOut,
  };
  // Who may flip each status (the API enforces the same rules).
  const canChange = (key: StatusKey) => (key === 'limited' ? canManage : contact.can.edit);

  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: contactKeys.all }),
      client.invalidateQueries({ queryKey: ['privacy'] }),
    ]);

  const setStatus = async (key: StatusKey, on: boolean, note = '') => {
    try {
      await api(`/contacts/${contact.id}/privacy`, {
        method: 'PATCH',
        body: statusBody(key, on, note),
      });
      await refresh();
      setAsking(null);
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };

  return (
    <Card className="space-y-4">
      <h2 className="font-semibold">Privacidad</h2>
      <ul className="space-y-2 text-sm">
        {(Object.keys(STATUS_TEXT) as StatusKey[]).map((key) => {
          const on = since[key] !== null;
          const text = STATUS_TEXT[key];
          return (
            <li key={key} className="flex min-h-11 items-center justify-between gap-3">
              <div>
                <p className={on ? 'font-medium' : 'text-muted-foreground'}>{text.label}</p>
                {on && (
                  <p className="text-xs text-muted-foreground">
                    Desde {formatDate(since[key] ?? '', timeZone)}
                  </p>
                )}
              </div>
              {canChange(key) && (
                <Button
                  size="sm"
                  variant={on ? 'ghost' : 'secondary'}
                  aria-label={`${on ? text.off : text.on}: ${text.label}`}
                  onClick={() => {
                    if (!on && text.note) setAsking(key);
                    else void setStatus(key, !on);
                  }}
                >
                  {on ? text.off : text.on}
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      <div className="space-y-2 border-t border-border pt-4">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium">Consentimientos</h3>
          {contact.can.edit && !isLimited && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                setAdding(true);
              }}
            >
              <Plus /> Registrar
            </Button>
          )}
        </div>
        {consents.data?.length === 0 && (
          <p className="text-sm text-muted-foreground">Sin consentimientos registrados.</p>
        )}
        <ul className="divide-y divide-border text-sm">
          {consents.data?.map((c) => (
            <li key={c.id} className="flex flex-col gap-2 py-2 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1 space-y-0.5">
                <p className="flex flex-wrap items-center gap-1.5 font-medium">
                  {CONSENT_PURPOSES[c.purpose].label}
                  {c.revocation ? (
                    <Badge tone="danger">Revocado</Badge>
                  ) : (
                    <Badge tone="success">Vigente</Badge>
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {LEGAL_BASES[c.legalBasis]} · {CONSENT_SOURCES[c.source]} ·{' '}
                  {formatDate(c.grantedAt, timeZone)}
                  {c.noticeVersion ? ` · aviso v${String(c.noticeVersion)}` : ''}
                </p>
                {c.evidence && <p className="text-xs text-muted-foreground">{c.evidence}</p>}
                {c.revocation && (
                  <p className="text-xs text-muted-foreground">
                    Revocado el {formatDate(c.revocation.at, timeZone)}
                    {c.revocation.completedAt
                      ? ' · aplicado'
                      : ` · aplicar antes del ${formatDay(c.revocation.dueOn)}`}
                  </p>
                )}
              </div>
              {!c.revocation && contact.can.edit && (
                <Button
                  size="sm"
                  variant="ghost"
                  aria-label={`Revocar ${CONSENT_PURPOSES[c.purpose].label}`}
                  onClick={() => {
                    setRevoking(c);
                  }}
                >
                  Revocar
                </Button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <ContactRequests contact={contact} />
      <ContactRightsActions
        contact={{
          id: contact.id,
          name: contact.name,
          anonymized: contact.privacy.anonymizedAt !== null,
        }}
      />

      {asking && (
        <NoteDialog
          title={STATUS_TEXT[asking].label}
          label={STATUS_TEXT[asking].note ?? ''}
          confirm={STATUS_TEXT[asking].on}
          onClose={() => {
            setAsking(null);
          }}
          onConfirm={(note) => setStatus(asking, true, note)}
        />
      )}
      {adding && (
        <ConsentDialog
          contactId={contact.id}
          onClose={() => {
            setAdding(false);
          }}
          onDone={refresh}
        />
      )}
      {revoking && (
        <RevokeDialog
          contactId={contact.id}
          consent={revoking}
          onClose={() => {
            setRevoking(null);
          }}
          onDone={refresh}
        />
      )}
    </Card>
  );
}

function NoteDialog({
  title,
  label,
  confirm,
  onClose,
  onConfirm,
}: {
  title: string;
  label: string;
  confirm: string;
  onClose: () => void;
  onConfirm: (note: string) => Promise<void>;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <Dialog open title={title} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setBusy(true);
          void onConfirm(note.trim()).finally(() => {
            setBusy(false);
          });
        }}
      >
        <Field label={label}>
          {(a11y) => (
            <textarea
              {...a11y}
              rows={3}
              maxLength={500}
              className={textarea}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="brand" loading={busy} disabled={!note.trim()}>
            {confirm}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function ConsentDialog({
  contactId,
  onClose,
  onDone,
}: {
  contactId: string;
  onClose: () => void;
  onDone: () => Promise<unknown>;
}) {
  const notify = useToast();
  const [purpose, setPurpose] = useState<ConsentPurpose>('commercial');
  const [legalBasis, setLegalBasis] = useState<LegalBasis>('consent');
  const [source, setSource] = useState<ConsentSource>('verbal');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const parsed = consentSchema.safeParse({
      purpose,
      legalBasis,
      source,
      ...(evidence.trim() ? { evidence } : {}),
    });
    if (!parsed.success) return;
    setBusy(true);
    try {
      await api(`/contacts/${contactId}/consents`, { method: 'POST', body: parsed.data });
      await onDone();
      notify('Consentimiento registrado.', 'success');
      onClose();
    } catch (error) {
      notify(errorMessage(error), 'error');
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title="Registrar consentimiento"
      description="Si la base es el consentimiento, queda asociado a la versión vigente del aviso de privacidad."
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Field label="Finalidad" hint={CONSENT_PURPOSES[purpose].help}>
          {(a11y) => (
            <Select
              {...a11y}
              value={purpose}
              onChange={(e) => {
                setPurpose(e.target.value as ConsentPurpose);
              }}
            >
              {Object.entries(CONSENT_PURPOSES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Base legal">
          {(a11y) => (
            <Select
              {...a11y}
              value={legalBasis}
              onChange={(e) => {
                setLegalBasis(e.target.value as LegalBasis);
              }}
            >
              {Object.entries(LEGAL_BASES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Cómo se obtuvo">
          {(a11y) => (
            <Select
              {...a11y}
              value={source}
              onChange={(e) => {
                setSource(e.target.value as ConsentSource);
              }}
            >
              {Object.entries(CONSENT_SOURCES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field
          label="Evidencia (opcional)"
          hint="Dónde puede verificarse: documento, formulario, fecha de la llamada."
        >
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={500}
              value={evidence}
              onChange={(e) => {
                setEvidence(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="brand" loading={busy}>
            Registrar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function RevokeDialog({
  contactId,
  consent,
  onClose,
  onDone,
}: {
  contactId: string;
  consent: Consent;
  onClose: () => void;
  onDone: () => Promise<unknown>;
}) {
  const notify = useToast();
  const client = useQueryClient();
  const [source, setSource] = useState<ConsentSource>('verbal');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const purpose = CONSENT_PURPOSES[consent.purpose].label;

  const revoke = async () => {
    setBusy(true);
    try {
      await api(`/contacts/${contactId}/consents/${consent.id}/revoke`, {
        method: 'POST',
        body: { source, ...(note.trim() ? { note } : {}) },
      });
      await Promise.all([
        onDone(),
        client.invalidateQueries({ queryKey: privacyKeys.revocations }),
      ]);
      notify('Consentimiento revocado.', 'success');
      onClose();
    } catch (error) {
      notify(errorMessage(error), 'error');
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={`Revocar: ${purpose}`}
      description={`En el sistema surte efecto de inmediato. Lo que haya que hacer fuera (avisar a colaboradores, listas de envío) debe completarse en ${String(REVOCATION_BUSINESS_DAYS)} días hábiles; quien gestiona la privacidad recibe el aviso.`}
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void revoke();
        }}
      >
        <Field label="Cómo lo pidió">
          {(a11y) => (
            <Select
              {...a11y}
              value={source}
              onChange={(e) => {
                setSource(e.target.value as ConsentSource);
              }}
            >
              {Object.entries(CONSENT_SOURCES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Nota (opcional)">
          {(a11y) => (
            <Input
              {...a11y}
              maxLength={500}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="danger" loading={busy}>
            Revocar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
